import { useEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { CircleNotchIcon } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useEmailThread } from '@/api/communications/email-threads/useEmailThread';
import { useActivityGraphContext } from '../ActivityGraphContext';
import {
  ActivityEmailCard,
  ActivityEmailSplitDocuments,
} from './ActivityEmailCard';

interface EmailsTabProps {
  conversationId?: string | null;
}

export function EmailsTab({ conversationId }: EmailsTabProps) {
  const gt = useGT();
  const context = useActivityGraphContext();
  // Emails whose split-child documents section is open. The thread query is
  // refetched with includeSplitDocuments whenever at least one is open.
  const [splitDocsExpandedEmails, setSplitDocsExpandedEmails] = useState<
    Set<string>
  >(new Set());

  // Reset split-doc expansion when the conversation changes so a newly
  // opened thread isn't fetched with includeSplitDocuments from stale state.
  const splitDocsConversationRef = useRef<string | null>(
    conversationId ?? null,
  );
  let activeSplitDocsExpandedEmails = splitDocsExpandedEmails;
  if (splitDocsConversationRef.current !== (conversationId ?? null)) {
    splitDocsConversationRef.current = conversationId ?? null;
    activeSplitDocsExpandedEmails = new Set();
    if (splitDocsExpandedEmails.size > 0) {
      setSplitDocsExpandedEmails(new Set());
    }
  }

  const {
    data: threadData,
    isLoading,
    isFetching,
    error,
  } = useEmailThread(
    conversationId ?? null,
    activeSplitDocsExpandedEmails.size > 0,
  );

  const toggleSplitDocuments = (emailId: string) => {
    setSplitDocsExpandedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };
  const emailRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const expandedEmailIds = context?.expandedEmailIds;
  const highlightedEmailId = context?.highlightedEmailId ?? null;

  // Track whether we've already auto-expanded for the current thread data,
  // so collapsing the last email doesn't immediately re-expand it.
  const [autoExpandedConversationId, setAutoExpandedConversationId] = useState<
    string | null
  >(null);

  // Auto-expand latest email once when thread first loads
  useEffect(() => {
    if (
      threadData?.emails?.length &&
      context &&
      expandedEmailIds?.size === 0 &&
      threadData.conversationId !== autoExpandedConversationId
    ) {
      const latestEmail = threadData.emails[threadData.emails.length - 1];
      context.toggleEmailExpanded(latestEmail.id);
      setAutoExpandedConversationId(threadData.conversationId);
    }
  }, [threadData, context, expandedEmailIds, autoExpandedConversationId]);

  // Scroll to highlighted email
  useEffect(() => {
    if (highlightedEmailId && emailRefs.current.has(highlightedEmailId)) {
      const element = emailRefs.current.get(highlightedEmailId);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });

      const timer = setTimeout(() => {
        context?.clearHighlights();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [highlightedEmailId, context]);

  const handleToggleExpanded = (emailId: string) => {
    context?.toggleEmailExpanded(emailId);
  };

  const handleExpandAll = () => {
    if (threadData?.emails && context) {
      context.expandAllEmails(threadData.emails.map((e) => e.id));
    }
  };

  const handleCollapseAll = () => {
    context?.collapseAllEmails();
  };

  const handleAttachmentClick = (documentId: string) => {
    context?.viewDocument(documentId);
  };

  if (!conversationId) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <T context="activity graph emails tab no conversation">
          No email conversation linked
        </T>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        <T context="error loading email thread">Error loading emails</T>
      </div>
    );
  }

  if (!threadData?.emails?.length) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <T context="empty email thread">No emails found</T>
      </div>
    );
  }

  const participantEmails = threadData.participantEmails?.join(', ') || '';

  return (
    <div className="p-3 space-y-3">
      {/* Header */}
      <div className="space-y-2">
        {participantEmails && (
          <div className="space-y-1">
            <span className="text-sm font-medium text-muted-foreground">
              {gt('Participants', {
                context: 'email thread participants label',
              })}
            </span>
            <p className="text-sm truncate">{participantEmails}</p>
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleExpandAll}>
            <T context="expand all emails in thread">Expand All</T>
          </Button>
          <Button variant="outline" size="sm" onClick={handleCollapseAll}>
            <T context="collapse all emails in thread">Collapse All</T>
          </Button>
        </div>
      </div>

      {/* Email list */}
      <div className="space-y-2">
        {threadData.emails.map((email) => (
          <ActivityEmailCard
            key={email.id}
            email={email}
            ref={(el) => {
              if (el) emailRefs.current.set(email.id, el);
              else emailRefs.current.delete(email.id);
            }}
            isExpanded={expandedEmailIds?.has(email.id) ?? false}
            isHighlighted={highlightedEmailId === email.id}
            onToggle={() => handleToggleExpanded(email.id)}
            onAttachmentClick={handleAttachmentClick}
            splitDocuments={
              <ActivityEmailSplitDocuments
                email={email}
                showSplitDocuments={activeSplitDocsExpandedEmails.has(email.id)}
                isFetching={isFetching}
                onToggleSplit={() => toggleSplitDocuments(email.id)}
                onAttachmentClick={handleAttachmentClick}
              />
            }
          />
        ))}
      </div>
    </div>
  );
}
